import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreateEventRegistration,
  CreateEventRegistrationFields,
  EventRegistrationForm,
  EventRegistrationFormField,
  EventRegistrationFormFieldType,
} from '../generated/api/dsv-public/model';
import { FieldComponent } from '../fields/FieldComponent';
import { Button, Stack, Typography } from '@mui/material';
import { fieldComponents } from '../fields/field.types';
import { PricingGroupFieldComponent } from '../option-fields/PricingGroupFieldComponent';
import { useTranslation } from 'react-i18next';
import { useFormCheckpoint } from '../context/FormCheckpoint';
import { extractFormValuesFromRegistrant, getPricingSummary } from '../util/helper';
import { useFormValidation } from '../context/FormValidation';
import { useEventRegistrationForm } from '../context/EventRegistrationForm';
import { SingleLineField } from '../fields/SingleLineField';
import { PricingSummary, RegistrantInfo } from './form.types';

type SingleRegistrationFormProps = {
  registrant?: RegistrantInfo;
  isSingularRegistration?: boolean;
  registrationForm?: EventRegistrationForm;
  prefilledFields?: CreateEventRegistrationFields;
  prefilledOptionValues?: number[];
  handleSubmitForm: (registrationValues: CreateEventRegistration) => void;
  handleUpdatePricingSummary?: (pricingSummary: PricingSummary[]) => void;
  handleGoBack?: () => void;
};

export const SingleRegistrationForm: React.FC<SingleRegistrationFormProps> = ({
  registrant,
  isSingularRegistration = true,
  registrationForm,
  prefilledFields = {},
  prefilledOptionValues = [],
  handleSubmitForm,
  handleUpdatePricingSummary,
  handleGoBack = () => {},
}) => {
  const { t } = useTranslation();
  const { scrollToError, isValidForm, fieldsValidation } = useFormValidation();
  const { savedRegistrationEntries } = useFormCheckpoint();
  const { optionsPriceMap, optionsCurrencyMap } = useEventRegistrationForm();

  const prefilledFieldValues = useMemo(() => {
    const extractedValuesFromRegistrant = extractFormValuesFromRegistrant(registrant, registrationForm);
    const prefilledFieldsInclRegistrantInfo = { ...prefilledFields, ...extractedValuesFromRegistrant };
    if (savedRegistrationEntries && !Array.isArray(savedRegistrationEntries)) {
      return { ...prefilledFieldsInclRegistrantInfo, ...savedRegistrationEntries.fields };
    }
    return prefilledFieldsInclRegistrantInfo;
  }, [registrant, registrationForm, prefilledFields, savedRegistrationEntries]);

  const prefilledOptions = useMemo(() => {
    const prefilledOptions: { [key: string]: number[] } = {};
    const isSingleSubmission = savedRegistrationEntries && !Array.isArray(savedRegistrationEntries);
    const checkpointValues = isSingleSubmission ? savedRegistrationEntries.options : prefilledOptionValues;
    registrationForm?.pricingOptionGroups?.forEach(pog => {
      const curGroupId = Number(pog.optionGroupId);
      const curGroupSelection =
        pog.options
          ?.filter(option => option.optionId && checkpointValues.includes(option.optionId))
          .map(option => Number(option.optionId)) || [];
      if (curGroupSelection) {
        prefilledOptions[curGroupId] = curGroupSelection;
      }
    });
    return prefilledOptions;
  }, [savedRegistrationEntries, prefilledOptionValues, registrationForm?.pricingOptionGroups]);

  const [fieldValues, setFieldValues] = useState<CreateEventRegistrationFields>(prefilledFieldValues);
  const [selectedPricingOptions, setSelectedPricingOptions] = useState<{ [key: string]: number[] }>(prefilledOptions);
  const [isSubmitPressed, setIsSubmitPressed] = useState<boolean>(false);

  const isValidPricingOptionSelection = useMemo(() => {
    if (registrationForm?.pricingOptionGroups === undefined) {
      return true;
    }
    for (const pog of registrationForm?.pricingOptionGroups) {
      const curGroupId = Number(pog.optionGroupId);
      const curGroupSelectionLength = selectedPricingOptions[curGroupId]?.length;
      if (
        pog.required &&
        ((pog.minOptions && curGroupSelectionLength < pog.minOptions) ||
          (pog.maxOptions && curGroupSelectionLength > pog.maxOptions) ||
          (pog.minOptions && !selectedPricingOptions[curGroupId]?.length && pog.minOptions > 0))
      ) {
        return false;
      }
    }
    return true;
  }, [registrationForm, selectedPricingOptions]);

  const requiredFieldsAreFilled: boolean = useMemo(() => {
    let requiredIsFilled = true;
    registrationForm?.fields?.forEach(field => {
      if (field.required && field.key && (!fieldValues[field.key] || fieldValues[field.key].length === 0)) {
        requiredIsFilled = false;
      }
    });
    return requiredIsFilled;
  }, [registrationForm, fieldValues]);

  const onSubmit = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      if (event) {
        setIsSubmitPressed(true);
        console.info('Form submitted with the following values:');
        console.info(JSON.stringify(fieldValues));
      }

      if (isSingularRegistration && !requiredFieldsAreFilled) {
        console.warn('Form invalid. Not all fields are filled.');
      }
      if (isSingularRegistration && !isValidForm) {
        console.warn('Form invalid. Field data is invalid.');
      }
      if (isSingularRegistration && !isValidPricingOptionSelection) {
        console.warn('Form invalid. Pricing options selection is invalid.');
      }
      if (isSingularRegistration && (!requiredFieldsAreFilled || !isValidForm || !isValidPricingOptionSelection)) {
        const invalidFields = fieldsValidation.filter(field => !field.isValid).map(field => field.fieldKey);
        console.warn('The following fields are invalid:', invalidFields);
        scrollToError();
        return false;
      }

      const options = Object.values(selectedPricingOptions).reduce((prev, cur) => prev.concat(cur), []);
      handleSubmitForm({ fields: fieldValues, options: options });
    },
    [
      fieldValues,
      selectedPricingOptions,
      fieldsValidation,
      isValidPricingOptionSelection,
      requiredFieldsAreFilled,
      isValidForm,
      isSingularRegistration,
      scrollToError,
      handleSubmitForm,
    ]
  );

  const onForcedSubmit = useCallback(() => {
    const options = Object.values(selectedPricingOptions).reduce((prev, cur) => prev.concat(cur), []);
    handleSubmitForm({ fields: fieldValues, options: options });
  }, [fieldValues, selectedPricingOptions, handleSubmitForm]);

  const onChangeFieldValue = useCallback((field: string, value: any) => {
    setFieldValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const onChangePricingOptionsValue = useCallback((optionGroupId: number, selectedOptions: number[]) => {
    setSelectedPricingOptions(prev => ({ ...prev, [optionGroupId]: selectedOptions }));
  }, []);

  useEffect(() => {
    const multiValueFields: string[] = [
      EventRegistrationFormFieldType.MULTI_SELECT,
      EventRegistrationFormFieldType.CHECKBOX,
    ];
    registrationForm?.fields?.forEach((field: EventRegistrationFormField) => {
      if (
        field.defaultValue !== undefined &&
        field.key !== undefined &&
        multiValueFields.includes(field.type as string)
      ) {
        setFieldValues(prev => ({ ...prev, [field.key as string]: [field.defaultValue] as any }));
      }
    });
  }, [registrationForm]);

  useEffect(() => {
    // submit on every change if it's not a singular form - i.e. it's an element in a multiform
    if (!isSingularRegistration && fieldValues && selectedPricingOptions) {
      onSubmit();
    }
  }, [isSingularRegistration, fieldValues, selectedPricingOptions, onSubmit]);

  const fieldsBeforeOptionGroups = registrationForm?.fields?.filter(
    field => field.options?.afterOptionGroups !== 'true'
  );

  const fieldsAfterOptionGroups = registrationForm?.fields?.filter(
    field => field.options?.afterOptionGroups === 'true'
  );

  const totalSelectedPricingOptionsSummary = useMemo(() => {
    const optionsPricingSummary = getPricingSummary(selectedPricingOptions, optionsPriceMap, optionsCurrencyMap);
    if (handleUpdatePricingSummary && optionsPricingSummary.size > 0) {
      handleUpdatePricingSummary(Array.from(optionsPricingSummary.values()));
    }
    return optionsPricingSummary;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPricingOptions]);

  const isError = (!requiredFieldsAreFilled || !isValidPricingOptionSelection || !isValidForm) && isSubmitPressed;

  return (
    <div className="registration-form--field-container">
      {isSingularRegistration && <h2>{t('forms.single-form.title')}</h2>}
      {fieldsBeforeOptionGroups?.map(field => {
        return (
          <FieldComponent
            key={field.key}
            componentType={fieldComponents[field.type as EventRegistrationFormFieldType] || SingleLineField}
            fieldProps={{ ...field, defaultValue: prefilledFieldValues[field.key as string] as any as string }}
            onChangeFieldValue={onChangeFieldValue}
          />
        );
      })}
      {registrationForm?.pricingOptionGroups?.map(optionGroup => {
        return (
          <PricingGroupFieldComponent
            key={optionGroup.optionGroupId}
            optionGroupProps={optionGroup}
            defaultValues={prefilledOptions[Number(optionGroup.optionGroupId)]}
            onChangeFieldValue={onChangePricingOptionsValue}
          />
        );
      })}
      {fieldsAfterOptionGroups?.map(field => {
        return (
          <FieldComponent
            key={field.key}
            componentType={fieldComponents[field.type as EventRegistrationFormFieldType]}
            fieldProps={{ ...field, defaultValue: prefilledFieldValues[field.key as string] as any as string }}
            onChangeFieldValue={onChangeFieldValue}
          />
        );
      })}
      {isSingularRegistration &&
      registrationForm?.pricingOptionGroups?.length &&
      totalSelectedPricingOptionsSummary.size ? (
        <Typography fontWeight="bold">
          Total:{' '}
          {Array.from(totalSelectedPricingOptionsSummary.values())
            .map(summary => `${summary.totalPrice.toFixed(2)} ${summary.currency}`)
            .join(', ')}
        </Typography>
      ) : (
        ''
      )}
      {isSingularRegistration && (
        <Stack direction="row" justifyContent="center" spacing={2}>
          <Button variant="outlined" onClick={handleGoBack}>
            {t('forms.common.back')}
          </Button>
          <Button
            onClick={onSubmit}
            onDoubleClick={onForcedSubmit}
            variant="outlined"
            color={isError ? 'error' : 'primary'}
          >
            {registrationForm?.billingAddressRequired ? t('forms.common.next') : t('forms.single-form.submit')}
          </Button>
        </Stack>
      )}
    </div>
  );
};
